import Router from "next/router";

interface RedirectToUrlArgs {
  url: string;
  queryParams?: Record<string, string>;
  replace?: boolean;
  openInNewTab?: boolean;
}

/**
 * Action to redirect to a URL (internal or external)
 * @param args - Configuration object
 * @param args.url - The URL to redirect to (required)
 * @param args.queryParams - Optional query parameters to append
 * @param args.replace - Whether to use router.replace instead of router.push for internal URLs (default: false)
 * @param args.openInNewTab - Whether to open URL in new tab (default: false)
 */
export const RedirectToUrl = (args: RedirectToUrlArgs) => {
  const {
    url,
    queryParams,
    replace = false,
    openInNewTab = false
  } = args || {};

  if (!url) {
    console.error("RedirectToUrl: url is required");
    return;
  }

  // Check if URL is external
  const isExternalUrl =
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("//");

  // Build final URL with query params if provided
  let finalUrl = url;
  if (queryParams && Object.keys(queryParams).length > 0) {
    if (isExternalUrl) {
      // For external URLs, use the URL directly
      const urlObj = new URL(url);
      Object.entries(queryParams).forEach(([key, value]) => {
        urlObj.searchParams.set(key, value);
      });
      finalUrl = urlObj.toString();
    } else {
      // For internal URLs, use current origin as base
      const baseUrl =
        typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost";
      const urlObj = new URL(url, baseUrl);
      Object.entries(queryParams).forEach(([key, value]) => {
        urlObj.searchParams.set(key, value);
      });
      finalUrl = urlObj.toString();
    }
  }

  // Handle external URLs
  if (isExternalUrl && typeof window !== "undefined") {
    if (openInNewTab) {
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = finalUrl;
    }
    return;
  }

  // Handle internal URLs using Next.js router
  const routerMethod = replace ? Router.replace : Router.push;

  if (queryParams && Object.keys(queryParams).length > 0) {
    const baseUrl =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost";
    const urlObj = new URL(url, baseUrl);
    const query: Record<string, string> = {};

    // Extract existing query params
    urlObj.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    // Merge provided query params
    Object.entries(queryParams).forEach(([key, value]) => {
      query[key] = value;
    });

    routerMethod({
      pathname: urlObj.pathname,
      query
    });
  } else {
    routerMethod(url);
  }
};

export default RedirectToUrl;
